import { Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { combineLatest, iif, map, of, pipe } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { NgbActiveModal, NgbDatepickerModule, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { LoaderComponent } from '@app/shared/components/loader/loader.component';
import { RentalAgreementDriverDetailsModel } from '../rental-agreement-drivers.model';
import { CountryService } from '@app/core/services/country.service';
import { IftaLabelModule } from 'primeng/iftalabel';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CountryAutocompleteDirective } from '@app/shared/directives/country-autocomplete.directive';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { RaVersion } from '../../rental-agreements.model';
import { newClientUid } from '@app/shared/utilities/client-uid.util';

interface DriverForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  licenseNumber: FormControl<string | null>;
  licenseDate: FormControl<Date | null>;
  licenseExpirationDate: FormControl<Date | null>;
  licensePlace: FormControl<string | null>;
  city: FormControl<string | null>;
  flightNumber: FormControl<string | null>;
  phone: FormControl<string | null>;
  mobile: FormControl<string | null>;
  email: FormControl<string | null>;
  birthPlace: FormControl<string | null>;
  address: FormControl<string | null>;
  zipCode: FormControl<string | null>;
  countryUid: FormControl<{ uid: string; label: string } | null>;
  birthDate: FormControl<Date | null>;
  documentType: FormControl<number>;
  documentNumber: FormControl<string | null>;
  documentIssueDate: FormControl<Date | null>;
  documentIssuePlace: FormControl<string | null>;
  isAdditionalDriver: FormControl<boolean>;
}

export type RentalAgreementDriversUpsertDialogComponentConfig =
  | { mode: 'create'; rentalAgreementUid: string; onlyAdditionalDriver: boolean }
  | { mode: 'update'; driver: RentalAgreementDriverDetailsModel; onlyAdditionalDriver: boolean }
  | { mode: 'readonly'; driver: RentalAgreementDriverDetailsModel; rentalAgreementVersion: RaVersion }
  | null;

@Component({
  standalone: true,
  selector: 'app-rental-agreement-drivers-upsert-dialog',
  templateUrl: './rental-agreement-drivers-upsert-dialog.component.html',
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    NgbNavModule,
    LoaderComponent,
    NgbDatepickerModule,
    IftaLabelModule,
    AutoCompleteModule,
    CountryAutocompleteDirective,
    DatePickerModule,
    SelectModule
  ],
  host: { class: 'd-flex flex-column flex-fill' }
})
export class RentalAgreementDriversUpsertDialogComponent implements OnInit {
  private _formBuilder = inject(NonNullableFormBuilder);
  private _modal = inject(NgbActiveModal);
  private _countryService = inject(CountryService);

  config = signal<RentalAgreementDriversUpsertDialogComponentConfig>(null);

  form: FormGroup<DriverForm>;
  item: RentalAgreementDriverDetailsModel | null = null;
  errors: string[] | null = null;

  isReadonly = () => this.config()?.mode === 'readonly';

  private _driverDetailsOperator = pipe(
    map(() => {
      const config = this.config();
      if (!config || config.mode === 'create') return null;
      return config.driver;
    })
  );

  private _selectedDriver$ = iif(
    () => this.config() !== null,
    of(true).pipe(
      this._driverDetailsOperator,
      map((driver) => {
        this.item = driver;
        this.initialize();
      })
    ),
    of(null)
  );

  vm$ = combineLatest([this._countryService.getAllCountries(), this._selectedDriver$]).pipe(
    map(([countries]) => ({ countries }))
  );

  constructor() {
    this.form = this._formBuilder.group<DriverForm>({
      firstName: this._formBuilder.control<string>('', [Validators.required]),
      lastName: this._formBuilder.control<string>('', [Validators.required]),
      licenseNumber: this._formBuilder.control<string | null>(null),
      licenseDate: this._formBuilder.control<Date | null>(null),
      licenseExpirationDate: this._formBuilder.control<Date | null>(null),
      licensePlace: this._formBuilder.control<string | null>(null),
      city: this._formBuilder.control<string | null>(null),
      flightNumber: this._formBuilder.control<string | null>(null, [Validators.maxLength(20)]),
      phone: this._formBuilder.control<string | null>(null),
      mobile: this._formBuilder.control<string | null>(null),
      email: this._formBuilder.control<string | null>(null, [Validators.email]),
      birthPlace: this._formBuilder.control<string | null>(null),
      address: this._formBuilder.control<string | null>(null),
      zipCode: this._formBuilder.control<string | null>(null, [Validators.maxLength(15)]),
      countryUid: this._formBuilder.control<{ uid: string; label: string } | null>(null),
      birthDate: this._formBuilder.control<Date | null>(null),
      documentType: this._formBuilder.control<number>(0),
      documentNumber: this._formBuilder.control<string | null>(null),
      documentIssueDate: this._formBuilder.control<Date | null>(null),
      documentIssuePlace: this._formBuilder.control<string | null>(null),
      isAdditionalDriver: this._formBuilder.control<boolean>(false)
    });
  }

  ngOnInit(): void {
    this.form.markAllAsTouched();
    const config = this.config();

    if (config?.mode === 'create' && config?.onlyAdditionalDriver) {
      this.form.controls.isAdditionalDriver.setValue(true);
      this.form.controls.isAdditionalDriver.disable();
    }
  }

  submit() {
    this.errors = null;
    if (this.form.invalid) return;

    this._modal.close(this.createModelValues());
  }

  dismiss() {
    this._modal.dismiss();
  }

  private initialize() {
    if (!this.item) return;
    const config = this.config();
    this.form.setValue({
      firstName: this.item.firstName,
      lastName: this.item.lastName,
      licenseNumber: this.item.licenseNumber,
      licenseDate: this.item.licenseDate ? new Date(this.item.licenseDate) : null,
      licenseExpirationDate: this.item.licenseExpirationDate ? new Date(this.item.licenseExpirationDate) : null,
      licensePlace: this.item.licensePlace,
      city: this.item.city,
      flightNumber: this.item.flightNumber,
      phone: this.item.phone,
      mobile: this.item.mobile,
      email: this.item.email,
      birthPlace: this.item.birthPlace,
      address: this.item.address,
      zipCode: this.item.zipCode,
      countryUid: null,
      birthDate: this.item.birthDate ? new Date(this.item.birthDate) : null,
      documentType: this.item.documentType,
      documentNumber: this.item.documentNumber,
      documentIssueDate: this.item.documentIssueDate ? new Date(this.item.documentIssueDate) : null,
      documentIssuePlace: this.item.documentIssuePlace,
      isAdditionalDriver: this.item.isAdditionalDriver
    });

    if (this.item.countryUid) {
      this._countryService.getAllCountries().subscribe((countries) => {
        const country = countries.find((c) => c.uid === this.item!.countryUid);
        if (country) {
          this.form.controls.countryUid.setValue({ uid: country.uid, label: country.countryName });
        }
      });
    }

    this.form.markAsUntouched();
    if (config?.mode === 'readonly') {
      this.form.disable();
    }
    if (config?.mode === 'update' && config?.onlyAdditionalDriver && this.item.isAdditionalDriver) {
      this.form.controls.isAdditionalDriver.disable();
    }
  }

  private createModelValues() {
    const formData = this.form.getRawValue();
    const config = this.config();
    return {
      ...formData,
      countryUid: formData.countryUid?.uid ?? null,
      birthDate: formData.birthDate ? this.formatDate(formData.birthDate) : null,
      licenseDate: formData.licenseDate ? this.formatDate(formData.licenseDate) : null,
      licenseExpirationDate: formData.licenseExpirationDate ? this.formatDate(formData.licenseExpirationDate) : null,
      documentIssueDate: formData.documentIssueDate ? this.formatDate(formData.documentIssueDate) : null,
      uid: config?.mode === 'create' ? newClientUid() : config?.driver.uid
    };
  }

  private formatDate(dateTime: Date): string {
    return new Date(
      Date.UTC(
        dateTime.getFullYear(),
        dateTime.getMonth(),
        dateTime.getDate(),
        dateTime.getHours(),
        dateTime.getMinutes(),
        dateTime.getSeconds(),
        dateTime.getMilliseconds()
      )
    )
      .toISOString()
      .substring(0, 19);
  }
}
